/* eslint-disable */
import { takeLatest, call, put } from 'redux-saga/effects';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import Constant from '../../Constants/Constants';
import Auth, { loginSuccess, loginFailure, loginRequest } from '../../Actions/Auth/Auth';
import LoginRequest from '../../../config';
import JwtAuthService from '../../../services/JwtAuthService';
import { removeCookie, setCookie } from '../../../utils/AllCookies';
import CryptoJS from 'crypto-js';
const SECRET_KEY = 'fDrtOnVl3t8OwV+0JjNDsQ==';
const LOGIN_ENDPOINT = 'api/account/login';
const AUTH_COOKIE_EXPIRATION_HOURS = 12;

function* logoutSaga(action) {
  removeCookie(); // Clear all cookies
  const { instance } = action.payload;

  sessionStorage.clear();
  localStorage.clear();
  yield call([instance, instance.logout]);
}

/**
 * Acquires an access token silently, falling back to a redirect when Azure AD
 * requires user interaction (for example, pending consent).
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance MSAL instance.
 * @param {import("@azure/msal-browser").SilentRequest} request Base silent request (scopes).
 * @param {import("@azure/msal-browser").AccountInfo} account Active account.
 * @returns {Generator<*, string | null, *>} The access token, or `null` when a
 * redirect was triggered (the page will reload); callers must stop in that case.
 */
function* acquireToken(instance, request, account) {
  try {
    const response = yield call([instance, instance.acquireTokenSilent], { ...request, account });
    return response?.accessToken ?? null;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent acquisition failed; interaction is required. The redirect reloads the page.
      yield call([instance, instance.acquireTokenRedirect], { ...request, account });
      return null;
    }
    throw error;
  }
}

/**
 * Acquires the Graph and HUB API tokens, then triggers the login request.
 *
 * The Graph token is forwarded to the back end (which uses it to read the
 * user's profile and groups); the API token authenticates the call itself via
 * the Authorization header.
 *
 * @param {{ payload: { instance: import("@azure/msal-browser").IPublicClientApplication, activeAccount: import("@azure/msal-browser").AccountInfo } }} action
 */
function* getDetailsSaga(action) {
  try {
    const { instance, activeAccount } = action.payload;

    // Graph token: used by the back end to read the user's profile and groups.
    const graphAccessToken = yield call(acquireToken, instance, LoginRequest.graphRequest, activeAccount);
    if (!graphAccessToken) return;

    // HUB API token: authenticates the request to the back end.
    const apiAccessToken = yield call(acquireToken, instance, LoginRequest.apiRequest, activeAccount);
    if (!apiAccessToken) return;

    const headers = { Authorization: `Bearer ${apiAccessToken}` };
    const data = { GraphToken: graphAccessToken };

    yield put(loginRequest(data, headers));
    yield put(Auth.getDetailsSuccess(data));
  } catch (error) {
    console.error('Error in getDetailsSaga:', error);
    yield put(Auth.getDetailsFailure(error.message));
  }
}

function* hfhLogin(data) {
  try {
    const response = yield call(JwtAuthService.postApi, data?.payload, LOGIN_ENDPOINT, data?.headers);

    localStorage.setItem(Constant.AUTH_TOKEN, JSON.stringify(response));
    setCookie(Constant.AUTH_TOKEN, JSON.stringify(response), AUTH_COOKIE_EXPIRATION_HOURS);

    const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(response), SECRET_KEY).toString();
    localStorage.setItem('userData', encryptedData);

    yield put(loginSuccess(response));
  } catch (error) {
    yield put(loginFailure(error.message));
  }
}

export function* watchAuth() {
  yield takeLatest(Constant.LOGIN_REQUEST, hfhLogin);
  yield takeLatest(Constant.LOGOUT, logoutSaga);
  yield takeLatest(Constant.GET_DETAILS_REQUEST, getDetailsSaga);
}
